/**
 * DestinationDiscovery — cultural mini-guide for a destination.
 *
 * Two blocks:
 *   1. Cultural mini-guide (history, culture, fauna, gastronomy, legends…)
 *   2. "Sabías que…" — curiosity cards
 *
 * The AI guide experience is handled exclusively by Maya (floating widget
 * + Maya Premium), so no standalone assistant lives in this section.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import {
  BookOpen,
  Compass,
  Drumstick,
  Feather,
  Flame,
  Landmark,
  MapPinned,
  Sparkles,
  Users2,
  Wand2,
} from "lucide-react";

import {
  getDestinationGuide,
  type DestinationGuide,
  type GuideSectionKey,
} from "@/lib/destination-guide.functions";
import { normalizeLang } from "@/i18n";
import { detectTourPlaces } from "@/lib/tour-places";

const SECTION_ICONS: Record<GuideSectionKey, React.ComponentType<{ className?: string }>> = {
  history: Landmark,
  culture: BookOpen,
  traditions: Users2,
  fauna_flora: Feather,
  gastronomy: Drumstick,
  legends: Flame,
  customs: Compass,
  geography: MapPinned,
};


interface Props {
  title: string;
  location: string | null;
  summary: string | null;
  /** Itinerario real del producto: fuente de los lugares de la guía. */
  itinerary?: { title: string; body: string | null }[];
  highlights?: string[];
  meetingPoints?: string[];
}

export function DestinationDiscovery({ title, location, summary, itinerary, highlights, meetingPoints }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const [expanded, setExpanded] = useState(false);
  const places = detectTourPlaces({ title, itinerary, highlights, meetingPoints }).places.map((p) => p.name);



  // COSTE IA: la mini-guía cultural solo se genera cuando el viajero pulsa
  // "Leer más". Antes se generaba automáticamente en cada visita a la ficha.
  const { data, isLoading } = useQuery({
    queryKey: ["destination-guide", lang, title, location, places.join("|")],
    queryFn: () =>
      getDestinationGuide({ data: { title, location, summary, lang, places, generate: true } }),
    enabled: expanded,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const guide: DestinationGuide | null = data?.guide ?? null;


  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-card via-card to-primary/5">
      <header className="border-b border-border/60 bg-background/40 px-6 py-5 sm:px-8">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Mostrando México
        </div>
        <h2 className="mt-1 font-display text-2xl font-semibold sm:text-3xl">
          {t("tour.discover.heading")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("tour.discover.subheading")}
        </p>
      </header>

      <div className="space-y-8 px-6 py-7 sm:px-8">
        {expanded && isLoading && !guide && (
          <p className="text-sm text-muted-foreground">
            {t("tour.discover.loading")}
          </p>
        )}

        {(guide?.intro || summary) && (
          <p className="text-[1.0625rem] leading-[1.78] text-foreground/90 sm:text-[1.1rem]">
            {guide?.intro || summary}
          </p>
        )}

        <>
          {guide && (
              <>
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
                    expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                  aria-hidden={!expanded}
                >
                  <div className="min-h-0 overflow-hidden">
                    <div className="space-y-8 pt-2">
                      {guide.sections.length > 0 && (
                        <div className="grid gap-4 sm:grid-cols-2">
                          {guide.sections.map((s, i) => {
                            const Icon = SECTION_ICONS[s.key] ?? BookOpen;
                            const localizedTitle = t(
                              `tour.discover.sectionTitles.${s.key}`,
                              { defaultValue: s.title },
                            );
                            return (
                              <article
                                key={`${s.key}-${i}`}
                                className="rounded-2xl border border-border/70 bg-background/70 p-5 transition hover:border-primary/40 hover:shadow-sm"
                              >
                                <div className="flex items-center gap-2.5">
                                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                    <Icon className="h-4 w-4" />
                                  </span>
                                  <h3 className="text-base font-semibold tracking-tight" style={{ fontFamily: '"Manrope", "Inter", sans-serif' }}>
                                    {s.place ? `${s.place} · ${localizedTitle}` : localizedTitle}
                                  </h3>
                                </div>
                                <p className="mt-3 text-[0.95rem] leading-[1.7] text-foreground/85">
                                  {s.body}
                                </p>
                              </article>
                            );
                          })}
                        </div>
                      )}

                      {guide.didYouKnow.length > 0 && (
                        <div>
                          <div className="mb-3 flex items-center gap-2">
                            <Wand2 className="h-4 w-4 text-primary" />
                            <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: '"Manrope", "Inter", sans-serif' }}>
                              {t("tour.discover.didYouKnow")}
                            </h3>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {guide.didYouKnow.map((d, i) => (
                              <div
                                key={i}
                                className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm leading-relaxed text-foreground/90"
                              >
                                {d}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-md transition-all duration-300 hover:opacity-90 hover:shadow-lg sm:text-base"
                  aria-expanded={expanded}
                >
                  <span aria-hidden>📖</span>
                  {expanded
                    ? t("tour.discover.showLess", { defaultValue: lang === "fr" ? "Afficher moins" : "Mostrar menos" })
                    : t("tour.discover.readMore", { defaultValue: lang === "fr" ? "Lire la suite" : "Leer más" })}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
                    aria-hidden
                  />
                </button>

              </>
          )}

          {!guide && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-md transition-all duration-300 hover:opacity-90 hover:shadow-lg sm:text-base"
            >
              <span aria-hidden>📖</span>
              {t("tour.discover.readMore", { defaultValue: lang === "fr" ? "Lire la suite" : "Leer más" })}
              <ChevronDown className="h-4 w-4" aria-hidden />
            </button>
          )}
        </>

      </div>
    </section>
  );
}
